import { Accommodation, Day, Destination, Transport, TripConfig } from '../types';
import { getLocationEmoji, getCountryRegion } from './locationAssets';
import { getCountryFlag } from './countryFlag';
import { resolveTz } from './airportTimezones';
import { destinationForDate, destTitle, transportDuringVisit } from './destinationItems';
import { calendarDayNumber, getTripTiming } from './travelState';

/** "YYYY-MM-DD" -> "7 Aug", the same UTC-anchored format as Day.dateShort
 *  (noon UTC avoids a date shift in negative-offset timezones). */
export function shortDateLabel(dateStr: string): string {
  return new Date(`${dateStr}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

import { localToUTCMs } from "../../functions/src/bookingConflicts.mjs";
export { localToUTCMs } from "../../functions/src/bookingConflicts.mjs";

// Raw minutes, timezone-aware — the source of truth for a transport leg's
// duration (resolves each end's actual timezone via airport/station code or
// city rather than assuming both ends share one). Shared by Itinerary.tsx's
// TransportDetail/summary-row displays and DestinationScheduler.tsx's Day
// Planner transit legs, so linking a real Transport item always uses this
// same calculation rather than a separate, inferior guess.
export function calculatedDurationMinutes(
  t: { fromCode?: string; from?: string; fromCity?: string; toCode?: string; to?: string; toCity?: string },
  dep?: string, arr?: string, depTz?: string, arrTz?: string
): number | null {
  if (!dep || !arr) return null;
  let diffMinutes = 0;
  if (dep.includes('T') && arr.includes('T')) {
    const resolvedDepTz = resolveTz(depTz, t.fromCode, t.from, t.fromCity);
    const resolvedArrTz = resolveTz(arrTz, t.toCode, t.to, t.toCity);
    if (resolvedDepTz && resolvedArrTz) {
      // Each end in its own zone, at its own date — so daylight saving on
      // either side (and half-hour zones like Adelaide or Lord Howe) is exact.
      diffMinutes = (localToUTCMs(arr, resolvedArrTz) - localToUTCMs(dep, resolvedDepTz)) / 60000;
    } else if (resolvedDepTz || resolvedArrTz) {
      // One end known, the other not: subtracting clock times would silently
      // assume they share a zone. That's how Melbourne → Queenstown showed 5h
      // (it's about 3h: NZ is two hours ahead). Unknown beats wrong.
      return null;
    } else {
      // Neither end known — most often a domestic hop between places we have
      // no record of, where the clocks do agree. The best available answer.
      diffMinutes = (new Date(arr).getTime() - new Date(dep).getTime()) / 60000;
    }
    // A negative result here is bad data, not a journey — both ends carry a
    // date, so the only way to land before you left is an arrival date that
    // was never rolled forward on an overnight leg. Unknown beats wrong: the
    // itinerary rendered it as "-6h -30m" and the Day Planner turned it into
    // a slot of negative length.
    if (diffMinutes < 0) return null;
  } else {
    const [dH, dM] = dep.split(':').map(Number);
    const [aH, aM] = arr.split(':').map(Number);
    diffMinutes = (aH * 60 + aM) - (dH * 60 + dM);
    // No dates to go on, so an earlier arrival clock is the ordinary
    // overnight case rather than a mistake.
    if (diffMinutes < 0) diffMinutes += 24 * 60;
  }
  return isNaN(diffMinutes) ? null : Math.round(diffMinutes);
}

export const getHistoricAvg = (city: string, month: string): string => {
  const data: Record<string, Record<string, string>> = {
    // Europe
    "Paris": { "Jan": "7°C", "Feb": "8°C", "Mar": "12°C", "Apr": "16°C", "May": "20°C", "Jun": "23°C", "Jul": "26°C", "Aug": "25°C", "Sep": "21°C", "Oct": "16°C", "Nov": "11°C", "Dec": "8°C" },
    "London": { "Jan": "9°C", "Feb": "9°C", "Mar": "12°C", "Apr": "15°C", "May": "18°C", "Jun": "21°C", "Jul": "23°C", "Aug": "23°C", "Sep": "20°C", "Oct": "16°C", "Nov": "12°C", "Dec": "9°C" },
    "Berlin": { "Jan": "3°C", "Feb": "4°C", "Mar": "9°C", "Apr": "14°C", "May": "19°C", "Jun": "22°C", "Jul": "24°C", "Aug": "24°C", "Sep": "19°C", "Oct": "14°C", "Nov": "8°C", "Dec": "4°C" },
    "Rome": { "Jan": "12°C", "Feb": "13°C", "Mar": "16°C", "Apr": "19°C", "May": "24°C", "Jun": "28°C", "Jul": "31°C", "Aug": "31°C", "Sep": "27°C", "Oct": "22°C", "Nov": "17°C", "Dec": "13°C" },
    "Madrid": { "Jan": "10°C", "Feb": "12°C", "Mar": "16°C", "Apr": "18°C", "May": "22°C", "Jun": "28°C", "Jul": "33°C", "Aug": "33°C", "Sep": "28°C", "Oct": "20°C", "Nov": "14°C", "Dec": "10°C" },
    "Barcelona": { "Jan": "14°C", "Feb": "15°C", "Mar": "17°C", "Apr": "19°C", "May": "22°C", "Jun": "26°C", "Jul": "29°C", "Aug": "29°C", "Sep": "26°C", "Oct": "22°C", "Nov": "18°C", "Dec": "15°C" },
    "Amsterdam": { "Jan": "6°C", "Feb": "6°C", "Mar": "10°C", "Apr": "13°C", "May": "17°C", "Jun": "20°C", "Jul": "22°C", "Aug": "22°C", "Sep": "19°C", "Oct": "15°C", "Nov": "10°C", "Dec": "7°C" },
    "Athens": { "Jan": "13°C", "Feb": "14°C", "Mar": "16°C", "Apr": "20°C", "May": "25°C", "Jun": "30°C", "Jul": "33°C", "Aug": "33°C", "Sep": "29°C", "Oct": "24°C", "Nov": "19°C", "Dec": "15°C" },
    "Istanbul": { "Jan": "9°C", "Feb": "9°C", "Mar": "12°C", "Apr": "16°C", "May": "21°C", "Jun": "26°C", "Jul": "29°C", "Aug": "29°C", "Sep": "25°C", "Oct": "20°C", "Nov": "15°C", "Dec": "11°C" },
    "Split": { "Jun": "27°C", "Jul": "31°C", "Aug": "31°C" },
    "Hvar": { "Jun": "27°C", "Jul": "31°C", "Aug": "31°C" },
    "Amalfi": { "Jun": "27°C", "Jul": "30°C", "Aug": "30°C" },
    "Bodrum": { "Jun": "30°C", "Jul": "34°C", "Aug": "34°C" },
    "Antalya": { "Jun": "31°C", "Jul": "34°C", "Aug": "34°C" },
    "Cappadocia": { "Jun": "27°C", "Jul": "31°C", "Aug": "31°C" },
    "Edinburgh": { "Jun": "17°C", "Jul": "19°C", "Aug": "19°C" },
    "Copenhagen": { "Jun": "19°C", "Jul": "21°C", "Aug": "21°C" },
    "Bruges": { "Jun": "20°C", "Jul": "22°C", "Aug": "22°C" },
    
    // North America
    "New York": { "Jan": "4°C", "Feb": "5°C", "Mar": "10°C", "Apr": "16°C", "May": "22°C", "Jun": "26°C", "Jul": "29°C", "Aug": "28°C", "Sep": "24°C", "Oct": "18°C", "Nov": "12°C", "Dec": "7°C" },
    "Los Angeles": { "Jan": "20°C", "Feb": "21°C", "Mar": "21°C", "Apr": "23°C", "May": "23°C", "Jun": "26°C", "Jul": "28°C", "Aug": "29°C", "Sep": "28°C", "Oct": "26°C", "Nov": "23°C", "Dec": "20°C" },
    "Chicago": { "Jan": "-1°C", "Feb": "1°C", "Mar": "7°C", "Apr": "14°C", "May": "21°C", "Jun": "26°C", "Jul": "29°C", "Aug": "28°C", "Sep": "24°C", "Oct": "17°C", "Nov": "9°C", "Dec": "2°C" },
    "Miami": { "Jan": "24°C", "Feb": "25°C", "Mar": "26°C", "Apr": "28°C", "May": "30°C", "Jun": "31°C", "Jul": "32°C", "Aug": "32°C", "Sep": "31°C", "Oct": "29°C", "Nov": "27°C", "Dec": "25°C" },
    "Toronto": { "Jan": "-1°C", "Feb": "0°C", "Mar": "5°C", "Apr": "12°C", "May": "19°C", "Jun": "24°C", "Jul": "27°C", "Aug": "26°C", "Sep": "21°C", "Oct": "14°C", "Nov": "8°C", "Dec": "2°C" },
    "Vancouver": { "Jan": "7°C", "Feb": "8°C", "Mar": "11°C", "Apr": "14°C", "May": "17°C", "Jun": "20°C", "Jul": "23°C", "Aug": "23°C", "Sep": "19°C", "Oct": "14°C", "Nov": "9°C", "Dec": "6°C" },
    "Mexico City": { "Jan": "21°C", "Feb": "23°C", "Mar": "26°C", "Apr": "27°C", "May": "27°C", "Jun": "25°C", "Jul": "23°C", "Aug": "24°C", "Sep": "23°C", "Oct": "23°C", "Nov": "22°C", "Dec": "21°C" },

    // Asia & Japan
    "Tokyo": { "Jan": "10°C", "Feb": "10°C", "Mar": "14°C", "Apr": "19°C", "May": "23°C", "Jun": "26°C", "Jul": "30°C", "Aug": "31°C", "Sep": "27°C", "Oct": "22°C", "Nov": "17°C", "Dec": "12°C" },
    "Osaka": { "Jan": "10°C", "Feb": "10°C", "Mar": "14°C", "Apr": "20°C", "May": "25°C", "Jun": "28°C", "Jul": "32°C", "Aug": "33°C", "Sep": "29°C", "Oct": "23°C", "Nov": "18°C", "Dec": "12°C" },
    "Kyoto": { "Jan": "9°C", "Feb": "10°C", "Mar": "14°C", "Apr": "20°C", "May": "25°C", "Jun": "28°C", "Jul": "32°C", "Aug": "33°C", "Sep": "29°C", "Oct": "23°C", "Nov": "17°C", "Dec": "12°C" },
    "Fukuoka": { "Jan": "10°C", "Feb": "11°C", "Mar": "15°C", "Apr": "20°C", "May": "24°C", "Jun": "27°C", "Jul": "31°C", "Aug": "32°C", "Sep": "28°C", "Oct": "23°C", "Nov": "18°C", "Dec": "13°C" },
    "Sapporo": { "Jan": "-1°C", "Feb": "0°C", "Mar": "4°C", "Apr": "11°C", "May": "17°C", "Jun": "21°C", "Jul": "25°C", "Aug": "26°C", "Sep": "22°C", "Oct": "16°C", "Nov": "8°C", "Dec": "2°C" },
    "Seoul": { "Jan": "2°C", "Feb": "5°C", "Mar": "11°C", "Apr": "18°C", "May": "24°C", "Jun": "27°C", "Jul": "29°C", "Aug": "30°C", "Sep": "26°C", "Oct": "20°C", "Nov": "12°C", "Dec": "4°C" },
    "Bangkok": { "Jan": "32°C", "Feb": "33°C", "Mar": "34°C", "Apr": "35°C", "May": "34°C", "Jun": "33°C", "Jul": "33°C", "Aug": "33°C", "Sep": "32°C", "Oct": "32°C", "Nov": "32°C", "Dec": "31°C" },
    "Singapore": { "Jan": "30°C", "Feb": "31°C", "Mar": "32°C", "Apr": "32°C", "May": "32°C", "Jun": "31°C", "Jul": "31°C", "Aug": "31°C", "Sep": "31°C", "Oct": "31°C", "Nov": "31°C", "Dec": "30°C" },
    "Hong Kong": { "Jan": "19°C", "Feb": "20°C", "Mar": "22°C", "Apr": "25°C", "May": "29°C", "Jun": "30°C", "Jul": "31°C", "Aug": "31°C", "Sep": "30°C", "Oct": "28°C", "Nov": "24°C", "Dec": "20°C" },
    "Bali": { "Jan": "31°C", "Feb": "31°C", "Mar": "31°C", "Apr": "32°C", "May": "32°C", "Jun": "31°C", "Jul": "30°C", "Aug": "30°C", "Sep": "31°C", "Oct": "32°C", "Nov": "32°C", "Dec": "31°C" },

    // Oceania
    "Sydney": { "Jan": "27°C", "Feb": "27°C", "Mar": "26°C", "Apr": "24°C", "May": "21°C", "Jun": "18°C", "Jul": "17°C", "Aug": "19°C", "Sep": "22°C", "Oct": "24°C", "Nov": "25°C", "Dec": "26°C" },
    "Melbourne": { "Jan": "26°C", "Feb": "26°C", "Mar": "24°C", "Apr": "21°C", "May": "17°C", "Jun": "14°C", "Jul": "14°C", "Aug": "15°C", "Sep": "18°C", "Oct": "20°C", "Nov": "22°C", "Dec": "24°C" },
    "Auckland": { "Jan": "24°C", "Feb": "24°C", "Mar": "23°C", "Apr": "20°C", "May": "18°C", "Jun": "16°C", "Jul": "15°C", "Aug": "15°C", "Sep": "17°C", "Oct": "18°C", "Nov": "20°C", "Dec": "22°C" },

    // South America
    "Buenos Aires": { "Jan": "30°C", "Feb": "29°C", "Mar": "27°C", "Apr": "23°C", "May": "19°C", "Jun": "16°C", "Jul": "15°C", "Aug": "18°C", "Sep": "20°C", "Oct": "23°C", "Nov": "26°C", "Dec": "29°C" },
    "Rio de Janeiro": { "Jan": "31°C", "Feb": "31°C", "Mar": "31°C", "Apr": "28°C", "May": "26°C", "Jun": "25°C", "Jul": "25°C", "Aug": "26°C", "Sep": "26°C", "Oct": "27°C", "Nov": "28°C", "Dec": "30°C" },
    "Sao Paulo": { "Jan": "28°C", "Feb": "28°C", "Mar": "27°C", "Apr": "25°C", "May": "23°C", "Jun": "21°C", "Jul": "21°C", "Aug": "23°C", "Sep": "23°C", "Oct": "24°C", "Nov": "25°C", "Dec": "27°C" },
    "Lima": { "Jan": "26°C", "Feb": "27°C", "Mar": "27°C", "Apr": "24°C", "May": "21°C", "Jun": "19°C", "Jul": "18°C", "Aug": "18°C", "Sep": "19°C", "Oct": "20°C", "Nov": "22°C", "Dec": "24°C" },
    "Santiago": { "Jan": "29°C", "Feb": "29°C", "Mar": "26°C", "Apr": "22°C", "May": "18°C", "Jun": "14°C", "Jul": "14°C", "Aug": "16°C", "Sep": "19°C", "Oct": "22°C", "Nov": "25°C", "Dec": "28°C" },

    // Africa & MA
    "Cape Town": { "Jan": "27°C", "Feb": "27°C", "Mar": "26°C", "Apr": "23°C", "May": "20°C", "Jun": "18°C", "Jul": "18°C", "Aug": "18°C", "Sep": "20°C", "Oct": "22°C", "Nov": "24°C", "Dec": "25°C" },
    "Cairo": { "Jan": "19°C", "Feb": "21°C", "Mar": "24°C", "Apr": "29°C", "May": "33°C", "Jun": "35°C", "Jul": "35°C", "Aug": "35°C", "Sep": "33°C", "Oct": "30°C", "Nov": "25°C", "Dec": "21°C" },
    "Dubai": { "Jan": "24°C", "Feb": "26°C", "Mar": "29°C", "Apr": "34°C", "May": "39°C", "Jun": "41°C", "Jul": "42°C", "Aug": "42°C", "Sep": "40°C", "Oct": "36°C", "Nov": "31°C", "Dec": "26°C" },
    "Marrakesh": { "Jan": "19°C", "Feb": "21°C", "Mar": "24°C", "Apr": "26°C", "May": "29°C", "Jun": "33°C", "Jul": "38°C", "Aug": "38°C", "Sep": "33°C", "Oct": "28°C", "Nov": "23°C", "Dec": "20°C" },
  };
  // Normalise away diacritics (e.g. "São Paulo" → "Sao Paulo") so the lookup
  // isn't defeated by accented characters not matching this table's plain-ASCII
  // keys — this table has no accented keys, so only the incoming city needs it.
  const normalizedCity = city.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const val = (data[city] && data[city][month]) || (data[normalizedCity] && data[normalizedCity][month]);
  if (!val) return "--";
  return `Usually ${val}`;
};

export const DEST_GRADIENTS = [
  'linear-gradient(135deg, #134e4a 0%, #042f2e 100%)', // Teal
  'linear-gradient(135deg, #312e81 0%, #1e1b4b 100%)', // Indigo
  'linear-gradient(135deg, #4c1d95 0%, #2e1065 100%)', // Purple
  'linear-gradient(135deg, #064e3b 0%, #022c22 100%)', // Emerald
  'linear-gradient(135deg, #881337 0%, #4c0519 100%)', // Rose
  'linear-gradient(135deg, #164e63 0%, #083344 100%)', // Ocean
  'linear-gradient(135deg, #c2410c 0%, #9a3412 100%)', // Terracotta -- shifted a step lighter/more saturated (was orange-800->900) for less brown, more burnt-orange
  'linear-gradient(135deg, #701a75 0%, #4a044e 100%)', // Plum
  'linear-gradient(135deg, #1e3a8a 0%, #172554 100%)', // Navy
  'linear-gradient(135deg, #3f6212 0%, #365314 100%)'  // Moss
];

// The solid color a destination is tagged with in the itinerary (the first stop of
// its DEST_GRADIENTS entry) — reused wherever a destination needs a flat accent color
// instead of the full gradient, e.g. a calendar entry's left border.
export const destAccentColor = (destIdx: number): string => {
  const gradient = DEST_GRADIENTS[destIdx % DEST_GRADIENTS.length];
  return gradient.match(/#[0-9a-fA-F]{6}/)?.[0] || '#c5a059';
};

export const getExpandedDays = (config: TripConfig): Day[] => {
  if (calendarDayNumber(config.startDate) === null || calendarDayNumber(config.endDate) === null) return [];

  const startDate = new Date(config.startDate);
  const endDate = new Date(config.endDate);

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return [];
  if (startDate > endDate) return [];

  const expanded: Day[] = [];
  let current = new Date(startDate);
  let dayCounter = 1;

  const sortedDestinations = [...(config.destinations || [])].sort((a, b) => new Date(a.arrivalDate).getTime() - new Date(b.arrivalDate).getTime());


  while (current <= endDate) {
    const isoDate = current.toISOString().split('T')[0];
    const dateShortStr = current.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    const monthPart = current.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });

    const destIdx = sortedDestinations.indexOf(destinationForDate(sortedDestinations, isoDate));
    
    let currentDest = destIdx !== -1 ? sortedDestinations[destIdx] : null;

    let dayData: Day;

    if (!currentDest) {
      dayData = {
        id: dayCounter,
        date: isoDate,
        dateShort: dateShortStr,
        dateFull: current.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }),
        dayNum: dayCounter,
        city: "Gap in Itinerary",
        emoji: "🗺️",
        flag: "",
        country: "",
        weather: "",
        nights: 0,
        desc: "No destination set for this date range.",
        cityBg: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
        accent: "#9ca3af",
        transports: [],
        nightsLeft: 0,
        accommodation: { 
          name: "", 
          stars: 0, 
          area: "", 
          checkIn: "", 
          checkOut: "", 
          address: "", 
          confirmRef: "", 
          confirmed: false, 
          isAirbnb: false 
        },
        accommodations: [],
        attractions: [],
        eats: [],
        nightlife: [],
        notes: [],
        travellers: config.travellers ? config.travellers.map(u => u.id) : [],
        destinationId: 'gap'
      };
    } else {
      // Every day is built from the destination itself. There used to be a
      // demo-data template (DAYS) matched here by a loose city-name `includes`
      // — empty for a long time, so the branch never ran, but repopulating it
      // would have handed "Paris, Texas" Paris's attractions.
      const cityBg = DEST_GRADIENTS[destIdx % DEST_GRADIENTS.length];
      {
        const cityName = destTitle(currentDest.name);
        const countryName = currentDest.country || currentDest.name.split(',').pop()?.trim() || 'Europe';
        
        dayData = {
          id: dayCounter,
          date: isoDate,
          dateShort: dateShortStr,
          dateFull: current.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }),
          dayNum: dayCounter,
          city: cityName,
          emoji: currentDest.emoji || getLocationEmoji(currentDest.name),
          // '' rather than a pin: Home renders "{flag} {country}", and a pin beside a
          // country name it couldn't place reads worse than nothing there.
          flag: getCountryFlag(currentDest.country || currentDest.name, ''),
          country: countryName,
          weather: getHistoricAvg(cityName, monthPart),
          nights: 0,
          desc: currentDest.blurb || `Exploring the hidden gems of ${currentDest.name}`,
          cityBg,
          accent: '#c5a059',
          transports: [],
          accommodation: { 
            name: "", 
            stars: 0, 
            area: "", 
            checkIn: "", 
            checkOut: "", 
            address: "", 
            confirmRef: "", 
            confirmed: false, 
            isAirbnb: false 
          },
          accommodations: [],
          attractions: [],
          eats: [],
          nightlife: [],
          notes: [],
          travellers: currentDest.travellerIds || config.travellers.map(u => u.id),
          destinationId: currentDest.name.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-')
        };
      }

      // nights remaining = how many overnight sleeps until departure.
      // departureDate is the day you leave (exclusive — you don't sleep there that night).
      // e.g. arrive Jun 25, depart Jun 29 → 4 nights (25-26, 26-27, 27-28, 28-29).
      // On Jun 25: 4 nights left. On Jun 28: 1 night left. On Jun 29 (departure): 0 nights left.
      const depMs = new Date(currentDest.departureDate.slice(0, 10) + 'T00:00').getTime();
      const curMs = new Date(isoDate + 'T00:00').getTime();
      const diffNights = Math.max(0, Math.round((depMs - curMs) / (1000 * 60 * 60 * 24)));
      dayData.nightsLeft = diffNights;

      // Total nights for the whole stay (arrival → departure) — fixed per
      // destination, unlike nightsLeft which counts down. Was previously left
      // at the hardcoded 0 from initialization above; nothing overwrote it
      // since every existing caller used nightsLeft (with `?? nights` only as
      // an always-false fallback), so the bug was invisible until something
      // started reading `nights` directly.
      const arrMs = new Date(currentDest.arrivalDate.slice(0, 10) + 'T00:00').getTime();
      dayData.nights = Math.max(0, Math.round((depMs - arrMs) / (1000 * 60 * 60 * 24)));
    }

    dayData.shopping ??= [];

    // Merge saved itinerary data if it exists — but only the fields that are
    // genuinely user-entered content for this day (transports, accommodations,
    // attractions, eats, nightlife, notes, travellers). Everything else on
    // dayData (nightsLeft, weather, dateShort, dayNum, city, destinationId,
    // emoji, ...) is derived fresh from config above and must stay that way:
    // a day doc persists the moment anything is saved for that date, so if we
    // ever blanket-spread `saved` over dayData, a destination's dates changing
    // later would silently un-fix itself back to the stale cached values
    // (this previously happened for nightsLeft specifically).
    if (config.itinerary && config.itinerary[isoDate]) {
      const saved = config.itinerary[isoDate];
      // Use the saved travellers when explicitly set by the user; fall back to
      // the live list from config so new members are included by default.
      const mergedTravellers = (saved.travellers && saved.travellers.length > 0)
        ? saved.travellers
        : dayData.travellers;
      dayData = {
        ...dayData,
        transports: saved.transports || dayData.transports,
        accommodation: saved.accommodation || dayData.accommodation,
        accommodations: saved.accommodations || dayData.accommodations,
        attractions: saved.attractions || dayData.attractions,
        eats: saved.eats || dayData.eats,
        nightlife: saved.nightlife || dayData.nightlife,
        shopping: saved.shopping || dayData.shopping,
        notes: saved.notes || dayData.notes,
        travellers: mergedTravellers,
      };
    }

    // Populate checking against global accommodations
    if (config.accommodations) {
       const todayAccs = config.accommodations.filter(acc => {
         if (!acc.checkIn || !acc.checkOut) return false;
         const inDate = acc.checkIn.split('T')[0];
         const outDate = acc.checkOut.split('T')[0];
         return isoDate >= inDate && isoDate < outDate;
       });
       if (todayAccs.length > 0) {
         dayData.accommodations = todayAccs;
       }
    }

    expanded.push(dayData);
    current.setUTCDate(current.getUTCDate() + 1);
    dayCounter++;
  }
  return expanded;
};

export const getFirstName = (name: string): string => {
  if (!name) return "";
  return name.split(' ')[0];
};

export const getTripStatus = (config: TripConfig, today?: string) => getTripTiming(config, today).status;

/**
 * Human label for where a trip goes: ≤3 countries are listed by name;
 * more than that collapse to regions, and trips longer than 7 days apply a
 * 15% day-share filter so a one-night stopover doesn't earn its own region.
 *
 * Shared by TripIdentitySummary (renders it as a gold chip) and Home's
 * feature card (renders it as serif italic under the divider) — extracted
 * here rather than duplicated, since it's ~50 lines of fiddly rules.
 */
/** Joins a list of names the way running prose does: "Brazil" for one,
 *  "Brazil & Argentina" for two, "Brazil, Argentina & Peru" for three or
 *  more — comma between all but the last pair, "&" (no comma) before the
 *  last. Used for the region pill's country/region list, which used to be
 *  a flat comma join regardless of count. */
const joinWithAnd = (items: string[]): string => {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return items.join(' & ');
  return `${items.slice(0, -1).join(', ')} & ${items[items.length - 1]}`;
};

export const getRegionsLabel = (config: TripConfig, itineraryDays: Day[]): string => {
  // ── Region pill label — same rules as the destination timeline elsewhere:
  // ≤3 countries show names; >3 map to regions; trips >7 days apply a 15%
  // day-share filter so a one-night stopover doesn't get its own region.
  const homeCountry = config?.homeCountry || (config?.homeLocation || '').split(',').pop()?.trim() || '';
  const hasNonHomeCountry = itineraryDays.some(d => d.country && d.country !== homeCountry);
  const isBookendOnly = (country: string) => {
    if (!hasNonHomeCountry) return false;
    if (!country || itineraryDays.length === 0) return false;
    return itineraryDays.every((d, i) => d.country !== country || i === 0 || i === itineraryDays.length - 1);
  };
  let uniqueCountries = Array.from(new Set(itineraryDays.map(d => d.country)))
    .filter(c => c !== "" && !(c === homeCountry && isBookendOnly(c)));
  if (uniqueCountries.length === 0 && (config?.destinations || []).length > 0) {
    uniqueCountries = Array.from(new Set(
      (config.destinations || []).map(d => d.country || d.name.split(',').pop()?.trim() || '').filter(Boolean)
    ));
  }
  const tripCalendarDays = (() => {
    const s = config?.startDate ? new Date(config.startDate) : null;
    const e = config?.endDate ? new Date(config.endDate) : null;
    if (s && e && !isNaN(s.getTime()) && !isNaN(e.getTime()))
      return Math.max(1, Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)));
    return itineraryDays.length || 1;
  })();
  let regionsLabel = "";
  if (uniqueCountries.length <= 3) {
    regionsLabel = joinWithAnd(uniqueCountries);
  } else if (tripCalendarDays > 7) {
    const regionDays: Record<string, number> = {};
    itineraryDays.forEach(day => {
      const region = day.country ? getCountryRegion(day.country) : undefined;
      if (region) regionDays[region] = (regionDays[region] ?? 0) + 1;
    });
    if (Object.keys(regionDays).length === 0) {
      (config?.destinations ?? []).forEach(dest => {
        const country = dest.country || dest.name.split(',').pop()?.trim() || '';
        const region = getCountryRegion(country);
        if (!region) return;
        let days = 1;
        if (dest.arrivalDate && dest.departureDate) {
          days = Math.max(1, Math.ceil(
            (new Date(dest.departureDate).getTime() - new Date(dest.arrivalDate).getTime()) / 86_400_000
          ));
        }
        regionDays[region] = (regionDays[region] ?? 0) + days;
      });
    }
    const qualifying = Object.entries(regionDays)
      .filter(([, days]) => days / tripCalendarDays >= 0.15)
      .sort(([, a], [, b]) => b - a)
      .map(([region]) => region);
    regionsLabel = joinWithAnd(qualifying);
    if (!regionsLabel) {
      const allRegions = new Set<string>();
      uniqueCountries.forEach(c => { const r = getCountryRegion(c); if (r) allRegions.add(r); });
      regionsLabel = joinWithAnd(Array.from(allRegions));
    }
  } else {
    const allRegions = new Set<string>();
    uniqueCountries.forEach(c => { const r = getCountryRegion(c); if (r) allRegions.add(r); });
    regionsLabel = joinWithAnd(Array.from(allRegions));
  }

  return regionsLabel;
};

// ─── Logistics gaps (accommodation + transport) ────────────────────────────
// Shared by Itinerary.tsx's TripOverview (the authoritative gap-fill UI) and
// Home.tsx's COUNTDOWN summary — extracted here so both read the same
// definition of "gap" instead of two hand-maintained copies drifting apart.

/** Whole nights between two date-only strings (never negative). */
export function nightsBetween(start: string, end: string): number {
  return Math.max(0, Math.round(
    (new Date(end.slice(0, 10) + 'T12:00:00').getTime() - new Date(start.slice(0, 10) + 'T12:00:00').getTime()) / 86_400_000
  ));
}

/** A destination's accommodations, clipped to its own stay and sorted by check-in. */
export function accsForDest(dest: Destination, accommodations: Accommodation[]): Accommodation[] {
  return accommodations
    .filter(acc => acc.checkIn && acc.checkOut && acc.checkIn.slice(0, 10) < dest.departureDate && acc.checkOut.slice(0, 10) > dest.arrivalDate)
    .sort((a, b) => (a.checkIn || '').localeCompare(b.checkIn || ''));
}

/**
 * Stretches of a destination's stay not covered by any of its accommodations.
 * `accs` is assumed already clipped/sorted by check-in (see accsForDest) —
 * clipping each booking to the destination's own bounds keeps a booking that
 * starts before arrival or ends after departure from producing a bogus
 * negative-length gap.
 */
export function accommodationGaps(dest: Destination, accs: Accommodation[]): { start: string; end: string; nights: number }[] {
  const gaps: { start: string; end: string; nights: number }[] = [];
  let cursor = dest.arrivalDate;
  for (const acc of accs) {
    const ci = (acc.checkIn || '').slice(0, 10);
    const co = (acc.checkOut || '').slice(0, 10);
    if (!ci || !co) continue;
    if (ci > cursor) {
      const nights = nightsBetween(cursor, ci);
      if (nights > 0) gaps.push({ start: cursor, end: ci, nights });
    }
    if (co > cursor) cursor = co;
  }
  if (cursor < dest.departureDate) {
    const nights = nightsBetween(cursor, dest.departureDate);
    if (nights > 0) gaps.push({ start: cursor, end: dest.departureDate, nights });
  }
  return gaps;
}

/** Total unbooked nights across every destination in the trip. */
export function totalAccommodationGapNights(config: TripConfig): number {
  const accommodations = config.accommodations || [];
  return (config.destinations || []).reduce((sum, dest) => {
    if (!dest.arrivalDate || !dest.departureDate) return sum;
    const gaps = accommodationGaps(dest, accsForDest(dest, accommodations));
    return sum + gaps.reduce((s, g) => s + g.nights, 0);
  }, 0);
}

/**
 * Whether any transport is booked for a date within [afterDate, beforeDate]
 * (inclusive on whichever bound is given; null means unbounded on that side).
 * Scoped deliberately to just the trip's own start/end boundaries (see
 * hasUnbookedTripBoundaryTransport) rather than every inter-destination leg —
 * matching those to the right pair of "real" (non-gap) destinations requires
 * the same city-grouping TripOverview builds for its own gap-fill CTAs, and
 * misreplicating that risks a confidently wrong "gap" for an edge case
 * (adjacent destinations, a still-unfilled date range between two stays,
 * etc.) — worse than not surfacing the mid-trip case at all. The two
 * boundary legs (getting there, getting home) are also the highest-stakes
 * pre-departure decisions, so they're the ones worth a Home surface first.
 */
function tripHasTransportInRange(config: TripConfig, afterDate: string | null, beforeDate: string | null): boolean {
  const all = Object.entries(config.itinerary || {}).flatMap(([date, day]) => (day?.transports || []).map(t => ({ ...t, _dayDate: date })));
  return Object.entries(config.itinerary || {}).some(([date, day]) =>
    (day?.transports || []).some((t: Transport) => {
      if (t.isRental || config.destinations?.some(dest => transportDuringVisit(t, dest, all))) return false;
      const d = t.dep ? t.dep.slice(0, 10) : date;
      if (afterDate && d < afterDate) return false;
      if (beforeDate && d > beforeDate) return false;
      return true;
    })
  );
}

/** Which of the trip's two transport boundaries (arrival, departure) has nothing booked. */
export function unbookedTripBoundaryTransport(config: TripConfig): { arrival: boolean; departure: boolean } {
  const arrival = !!config.startDate && !tripHasTransportInRange(config, null, config.startDate);
  const departure = !!config.endDate && !tripHasTransportInRange(config, config.endDate, null);
  return { arrival, departure };
}

/**
 * Which trip should be selected, given the user's current trips and selection.
 * Returns null when the current selection is still fine.
 *
 * `activeTripId` is cleared when *you* delete a trip, but nothing cleared it
 * when a trip left your list some other way — another member removing you, the
 * owner deleting it, access being revoked. It then kept pointing at a trip you
 * can no longer read, and because `activeTrip` separately falls back to
 * `trips[0]`, the app would display one trip while its expense/itinerary/
 * memory listeners subscribed to another. Those listeners fail with
 * permission-denied, so the visible trip's data never loads and the console
 * fills with retries.
 */
export function nextActiveTripId(
  trips: { id: string }[],
  activeTripId: string,
  hasPendingTrip: boolean,
): string | null {
  // No selection yet, or a freshly created trip that hasn't synced into the
  // list — leave both to the auto-select/creation flows.
  if (!activeTripId || hasPendingTrip) return null;
  // An empty list is ambiguous: still loading, or genuinely no trips. Either
  // way there's nothing better to point at, so don't churn the selection.
  if (trips.length === 0) return null;
  if (trips.some(t => t.id === activeTripId)) return null;
  // Match what `activeTrip` itself falls back to, so the displayed trip and
  // the listeners agree.
  return trips[0].id;
}

/** The clock time ("HH:MM") in a stored value — "2026-09-27T10:30" or "10:30" — or '' when it has none. */
export function clockTimeOf(raw?: string | null): string {
  const value = raw || '';
  const time = value.includes('T') ? value.split('T')[1] : value;
  return /^\d{1,2}:\d{2}/.test(time) ? time.slice(0, 5) : '';
}
